/**
 * AssetListItem Component
 * Custom implementation for displaying vault assets in table format
 * Based on Bitcoin.com React CL design patterns
 */

import { ReactNode } from 'react'

/**
 * Simple asset icon component with symbol-based colors
 */
function AssetIconWrapper({ symbol }: { symbol: string }) {
  // Color mapping for different assets
  const colorMap: Record<string, { bg: string; text: string }> = {
    'WETH': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
    'wstETH': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
    'wBTC': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
    'cbBTC': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
    'USDC': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  }
  
  const colors = colorMap[symbol] || { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' }
  
  // Display symbol (map WETH to ETH for display)
  const displaySymbol = symbol === 'WETH' ? 'ETH' : 
                        symbol === 'wstETH' ? 'stETH' :
                        symbol === 'wBTC' || symbol === 'cbBTC' ? 'BTC' :
                        symbol
  
  return (
    <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center font-bold text-xs ${colors.text}`}>
      {displaySymbol.slice(0, 3)}
    </div>
  )
}

interface AssetListItemProps {
  assetSymbol: string
  assetName: string
  balance: string
  valueUSD: string
  pricePerToken: string
  actions?: ReactNode
  showDivider?: boolean
  onClick?: () => void
}

export function AssetListItem({
  assetSymbol,
  assetName,
  balance,
  valueUSD,
  pricePerToken,
  actions,
  showDivider = true,
  onClick
}: AssetListItemProps) {
  return (
    <div className="flex flex-col items-start w-full">
      <div
        className={`
          flex items-center w-full overflow-clip p-m gap-m
          hover:bg-secondary-100/10 dark:hover:bg-secondary-100/20 transition-colors
          ${onClick ? 'cursor-pointer' : ''}
        `}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {/* Asset Icon */}
        <div className="relative flex-shrink-0 align-middle justify-center flex items-center size-[40px]">
          <AssetIconWrapper symbol={assetSymbol} />
        </div>

        {/* Left Content - Asset Name and Balance */}
        <div className="flex flex-col items-start flex-grow min-w-0">
          <div className="font-['Satoshi_Variable'] font-medium text-shades-black w-full text-[16px] leading-snug">
            {assetName}
          </div>
          <div className="font-['Satoshi_Variable'] font-medium text-shades-dark w-full mt-[4px] text-[14px] leading-snug">
            {balance}
          </div>
        </div>

        {/* Right Content - Value and Price */}
        <div className="flex flex-col items-end flex-shrink-0 text-right">
          <div className="font-['Satoshi_Variable'] font-medium text-shades-black text-[16px] leading-snug">
            ${valueUSD}
          </div>
          <div className="font-['Satoshi_Variable'] font-medium text-shades-dark mt-[4px] text-[14px] leading-snug">
            {pricePerToken}
          </div>
        </div>

        {/* Action Buttons */}
        {actions && <div className="flex-shrink-0 ml-4">{actions}</div>}
      </div>

      {/* Divider */}
      {showDivider && (
        <div className="w-full h-0 border-b border-shades-light dark:border-shades-semi-light" aria-hidden="true" />
      )}
    </div>
  )
}

